<template>
    <div class="forecast-invoice-wrapper">
        <CreateInvoiceModal v-if="showingCreateInvoice" @cancel="showingCreateInvoice = false"
                            @create="createInvoice" />
        <div class="forecast-invoice-wrapper-head">
            <div class="forecast-invoice-wrapper-head-title">
                {{ $t("billing.myInvoices") }}
            </div>
            <div class="forecast-invoice-wrapper-head-actions">
                <a-select
                    v-model="filters.type"
                    class="custom-select"
                    dropdownClassName="custom-select-dropdown"
                >
                    <a-select-option value="all">{{ $t("billing.allInvoices") }}</a-select-option>
                    <a-select-option value="manuel">{{ $t("billing.manualInvoices") }}</a-select-option>
                    <a-select-option value="monthly">{{ $t("billing.endOfPeriodInvoices") }}</a-select-option>
                    <a-select-option value="purchases">{{ $t("billing.purchaseInvoices") }}</a-select-option>
                    <a-select-option value="wallet-deposit">{{ $t("billing.walletDeposit") }}</a-select-option>
                    <a-select-option value="unpaid">{{ $t("billing.unpaidInvoices") }}</a-select-option>
                    <a-select-option value="paid">{{ $t("billing.paidInvoices") }}</a-select-option>
                </a-select>
                <SupsisButton v-if="isAdmin" @click="showingCreateInvoice = true">{{ $t("billing.createInvoice") }}</SupsisButton>
            </div>
        </div>
        <div class="forecast-invoice-wrapper-content">
            <supsis-table
                @cursor-changed="cursorChanged"
                :paginationCursor="paginationCursor"
                :columns="getInvoiceTableColumns()"
                :rows="allInvoices"
                :bottomPagination="true"
                :zebra-row="false"
            />
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from "vuex";
import {
    createInvoice,
    forceDeleteInvoice,
    getInvoices,
    markInvoiceAsPaid,
    refundInvoice,
    takePayment, updateInvoiceById,
} from "../../../../api/billing";
import SupsisTable from "../../../../components/SupsisTable.vue";
import PlanItemButton from "./PlanItemButton.vue";
import MoneyFormatter from "../../../../components/MoneyFormatter.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import CreateInvoiceModal from "../../../../components/billing/CreateInvoiceModal.vue";

export default {
    name: "ForecastInvoice",
    components: {
        CreateInvoiceModal,
        SupsisButton,
        SupsisTable,
        PlanItemButton,
        MoneyFormatter,
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            currentPlans: (state) => state.site.currentPlans,
            plans: (state) => state.site.plans,
            isAdmin: (state) => state.auth.adminUser != null,
        }),
        allInvoices() {
            const nextMonth = this.$moment().startOf("month").add(1, "month");
            return [
                this.totalForecastPrice && ["monthly", "unpaid", "all"].includes(this.filters.type) && {
                    _id: (nextMonth.valueOf() / 1000).toString(16).slice(0, 8),
                    invalidPayment: false,
                    iyzicoId: null,
                    price: this.totalForecastPrice,
                    createdDate: nextMonth,
                    pdf: null,
                    type: "forecast",
                },
                ...this.invoices,
            ].filter(Boolean);
        },
    },
    data: () => ({
        showingCreateInvoice: false,
        invoicePrice: 0,
        totalForecastPrice: null,
        invoices: [],
        filters: {
            type: "all",
        },
        paginationCursor: {
            page: 1,
            perPage: 10,
            total: 0,
        },
    }),
    methods: {
        ...mapActions({
            getRunningInvoice: "site/getRunningInvoice",
        }),
        cursorChanged({ page, perPage }) {
            this.paginationCursor.page = page;
            this.paginationCursor.perPage = perPage;
            this.fetchInvoices();
        },
        fetchInvoices() {
            const isShowingForecast = ["monthly", "unpaid", "all"].includes(this.filters.type);
            getInvoices(this.filters, {
                page: this.paginationCursor.page,
                perPage: isShowingForecast ? this.paginationCursor.perPage - 1 : this.paginationCursor.perPage,
            }).then((response) => {
                this.invoices = response.data.invoices;
                this.paginationCursor.total = response.data.total;
            });
        },
        getInvoiceTableColumns() {
            return [
                this.isAdmin && {
                    title: this.$t("billing.invalidBill"),
                    field: "invalidPayment",
                    key: "invalidPayment",
                    renderBodyCell: ({ row }) => {
                        const onChange = (event) => {
                            if (row.type === "forecast") {
                                this.$message.error(this.$t("billing.invalidBillNotPossible"));
                                return;
                            }
                            this.changeInvalidPayment(row._id, event.target.checked);
                        };
                        return <a-checkbox checked={row.invalidPayment} on-change={onChange} />;
                    },
                },
                this.isAdmin && {
                    title: this.$t("billing.paymentId"),
                    dataIndex: "iyzicoId",
                    key: "iyzicoId",
                    renderBodyCell: ({ row }) => {
                        return `${row.paymentTransactionId || "n/A"}`;
                    },
                },
                {
                    title: this.$t("billing.billType"),
                    field: "type",
                    key: "type",
                    renderBodyCell: ({ row }) => {
                        return <a-tag color="#1C1B1F"> {this.formatBillType(row.type)}</a-tag>;
                    },
                },
                {
                    title: this.$t("billing.invoiceId"),
                    field: "_id",
                    key: "_id",
                    renderBodyCell: ({ row }) => {
                        return row._id.slice(0, 8);
                    },
                },
                {
                    title: this.$t("billing.invoiceAmount"),
                    field: "price",
                    key: "price",
                    renderBodyCell: ({ row }) => {
                        return row.type === "forecast" ?
                            <span><MoneyFormatter price={row.price} />  (Tahmini)</span>
                            : <MoneyFormatter price={row.price} />;
                    },
                },
                {
                    title: this.$t("billing.invoiceDate"),
                    field: "createdDate",
                    key: "createdDate",
                    renderBodyCell: ({ row }) => {
                        return this.$moment(row.createdDate).format("YYYY/MM/DD");
                    },
                },
                {
                    title: this.$t("billing.invoiceStatus"),
                    field: "paid",
                    key: "paid",
                    renderBodyCell: ({ row }) => {
                        const PaymentIcon = () => {
                            return <div
                                class="payment-icon"
                                status={row.refund ? "refund" : row.paid ? "paid" : row.type === "forecast" ? "forecast" : "not-paid"}
                            >
                            </div>;
                        };
                        return <div class="payment-status">
                            <PaymentIcon />
                            {row.refund ? "İade Edildi" : row.paid ? "Ödendi" : row.type === "forecast" ? "Bekleniyor" : "Ödenmedi"}
                        </div>;
                    },
                },
                {
                    title: "",
                    field: "pdf",
                    key: "pdf",
                    align: "right",
                    renderBodyCell: ({ row }) => {
                        const downloadPdf = async () => {
                            const pdfUrl = "https://static.supsis.live/" + row.pdf;
                            window.open(pdfUrl, "_blank");
                        };
                        const showInvoice = () => {
                            if (row.type === "forecast") {
                                this.$router.push(`/billing/invoice/forecast`);
                                return;
                            }
                            this.$router.push(`/billing/invoice/${row._id}`);
                        };

                        const AdminActions = () => {
                            const DeleteInvoice = () => {
                                return <PlanItemButton on-click={() => this.deleteInvoice(row._id)}>
                                    Faturayı Sil
                                </PlanItemButton>;
                            };

                            const RefundInvoice = () => {
                                return !row.refund && <PlanItemButton on-click={() => this.refundInvoice(row._id)}>
                                    Geri Ödeme Yap
                                </PlanItemButton>;
                            };

                            const MarkAsPaid = () => {
                                return !row.paid && <PlanItemButton on-click={() => this.markAsPaid(row._id)}>
                                    Ödendi İşaretle
                                </PlanItemButton>;
                            };

                            const EditPrice = () => {
                                return <a-popconfirm
                                    placement="bottomRight"
                                    ok-text="Onayla"
                                    cancel-text="İptal"
                                    on-confirm={() => this.updateInvoicePrice(row._id, this.invoicePrice)}
                                >
                                    <a-icon slot="icon" type="question-circle-o" style="display: none" />
                                    <template slot="title">
                                        <a-input-number value={this.invoicePrice}
                                                        on-change={(value) => this.invoicePrice = value} />
                                    </template>
                                    <PlanItemButton>
                                        Fiyatı Düzenle
                                    </PlanItemButton>
                                </a-popconfirm>;
                            };

                            return row.type !== "forecast" && <div
                                style="display: flex; align-items: center; gap: 5px;">
                                <MarkAsPaid />
                                {row.paid && row.paymentTransactionId && <RefundInvoice />}
                                <DeleteInvoice />
                                <EditPrice />
                            </div>;
                        };

                        return <div style="display: flex; align-items: center; gap: 5px; justify-content: flex-end;">
                            {row.pdf && <PlanItemButton on-click={downloadPdf}>Fatura PDF</PlanItemButton>}
                            <PlanItemButton on-click={showInvoice}>Faturayı Görüntüle</PlanItemButton>
                            {
                                !row.paid && row.type !== "forecast" &&
                                <PlanItemButton on-click={() => this.getPayment(row._id)}>Ödemeyi Yap</PlanItemButton>
                            }
                            {this.isAdmin && <AdminActions />}
                        </div>;
                    },
                },

            ];
        },
        async changeInvalidPayment(invoiceId, value) {
            const success = await updateInvoiceById(invoiceId, {
                invalidPayment: value,
            }).then(() => true).catch(() => false);
            if (!success) {
                this.$notification.error({
                    message: "Fatura Güncelleme Hatası",
                    description: "Fatura geçersiz işaretleme işlemi sırasında bir hata oluştu.",
                });
                return;
            }
            this.fetchInvoices();
            this.$notification.success({
                message: "Fatura Güncellendi",
                description: "Fatura geçersiz olarak işaretlendi.",
            });
        },
        formatBillType(type) {
            const types = {
                none: "None",
                manuel: "Manuel",
                monthly: this.$t("billing.monthly"),
                "quick-purchase": "Paket Satın Alım",
                "wallet-deposit": this.$t("billing.balanceTopUp"),
                "forecast": "Yaklaşan Fatura",
            };
            return types[type];
        },
        async getPayment(invoiceId) {
            const response = await takePayment(invoiceId).then((res) => res.data).catch((e) => {
                return { status: "error", message: null };
            });
            if (response.status === "error") {
                this.$notification.error({
                    message: "Ödeme Başarısız",
                    description: response.message || "Ödeme işlemi sırasında bir hata oluştu.",
                });
                this.fetchInvoices();
                return;
            }
            this.$notification.success({
                message: "Ödeme Başarılı",
                description: "Ödeme işlemi başarılı bir şekilde gerçekleşti.",
            });
        },
        async deleteInvoice(invoiceId) {
            await this.$dialog.confirm(this.$t("message.deleteConfirm", { object: this.$t("billing.invoice") }), {
                okText: this.$t("message.continue"),
                cancelText: this.$t("message.cancel"),
            });
            await forceDeleteInvoice(this.site._id, invoiceId);
            this.fetchInvoices();
        },
        async refundInvoice(invoiceId) {
            await this.$dialog.confirm(this.$t("billing.areYouSoreRefundInvoice"), {
                okText: "Devam Et",
                cancelText: "İptal",
            });
            await refundInvoice(invoiceId);
            this.$notification.success({
                message: "Fatura İadesi",
                description: "Fatura başarılı bir şekilde iade edildi.",
            });
            this.fetchInvoices();
        },
        async markAsPaid(invoiceId) {
            await this.$dialog.confirm(this.$t("billing.areYouSoreRefundInvoice"), {
                okText: this.$t("message.continue"),
                cancelText: this.$t("message.cancel"),
            });
            const isSuccess = await markInvoiceAsPaid(this.site._id, invoiceId).then((res) => res.data).catch(() => false);
            if (!isSuccess) {
                this.$notification.error({
                    message: "Fatura Ödeme Hatası",
                    description: "Fatura ödeme işlemi sırasında bir hata oluştu.",
                });
                return;
            }
            this.$notification.success({
                message: "Fatura Ödendi",
                description: "Fatura başarılı bir şekilde ödendi olarak işaretlendi.",
            });
            this.fetchInvoices();
        },
        async updateInvoicePrice(invoiceId, price) {
            const success = await updateInvoiceById(invoiceId, {
                price,
            }).then(() => true).catch(() => false);
            if (!success) {
                this.$notification.error({
                    message: "Fatura Güncelleme Hatası",
                    description: "Fatura fiyatı güncelleme işlemi sırasında bir hata oluştu.",
                });
                return;
            }
            this.$notification.success({
                message: "Fatura Güncellendi",
                description: "Fatura fiyatı başarılı bir şekilde güncellendi.",
            });
            this.fetchInvoices();
        },
        async createInvoice(price, description, createdDate) {
            const invoice = createInvoice(price, description, createdDate)
                .then((res) => res.data).catch(() => null);
            if (!invoice) {
                this.$toasted.error(this.$t("billing.createdNewInvoiceFailed"), {
                    duration: 3000,
                    position: "top-center",
                });
                return;
            }
            this.$toasted.success(this.$t("billing.createdNewInvoice"), {
                duration: 3000,
                position: "top-center",
            });
            this.showingCreateInvoice = false;
            this.fetchInvoices();
        },
    },
    mounted() {
        const isSuspend = this.site.suspend;
        if (isSuspend) this.filters.type = "unpaid";
        this.getRunningInvoice(true).then((runningInvoice) => {
            this.totalForecastPrice = runningInvoice.endOfMonthForecastPrice;
        });
        this.fetchInvoices();
    },
    watch: {
        filters: {
            handler() {
                this.fetchInvoices();
            },
            deep: true,
        },
    },
};
</script>
<style lang="scss" scoped>
.forecast-invoice-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    padding: 20px;

    &-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        &-title {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 5px;
            font-size: 20px;
            font-weight: 600;
            color: #1C1B1F;

            svg {
                width: 20px;
                height: 20px;
            }
        }

        &-actions {
            display: flex;
            align-items: center;
            gap: 10px;

            .custom-select {
                width: 220px;
                border: 1px solid #D1D5DB;
                border-radius: 6px;
                transition: all 0.3s ease;

                &:hover {
                    border-color: #6B7280;
                }

                &::v-deep .ant-select-selection {
                    height: 40px;
                    display: flex;
                    align-items: center;
                    background: #F9FAFB;
                    border: none;
                    box-shadow: none;
                }

                &::v-deep .ant-select-selection__rendered {
                    margin-left: 12px;
                    font-size: 14px;
                    color: #374151;
                }

                &::v-deep .ant-select-arrow {
                    color: #6B7280;
                    right: 12px;
                }
            }

            .custom-select-dropdown {
                border-radius: 8px;
                box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);

                .ant-select-dropdown-menu-item {
                    padding: 8px 12px;
                    font-size: 14px;

                    &:hover {
                        background: #F3F4F6;
                    }
                }
            }
        }
    }

    &-content {
        width: 100%;
        background: #F1F4F7;
        border-radius: 8px;
        box-sizing: border-box;
        padding: 10px;

        .supsis-table {
            width: 100%;
        }

        .payment-status {
            display: flex;
            align-items: center;
            gap: 5px;

            .payment-icon {
                width: 10px;
                height: 10px;
                border-radius: 100px;

                &[status="paid"] {
                    background-color: #2ECC71;
                }

                &[status="refund"] {
                    background-color: #E74C3C;
                }

                &[status="forecast"] {
                    background-color: #F1C40F;
                }

                &[status="not-paid"] {
                    background-color: #E74C3C;
                }
            }
        }

    }
}
</style>
